import React, { useState, useCallback } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import classnames from 'classnames'
import { DatePicker, TimePicker, Button, Tooltip } from 'antd'
import CONFIG from '../../../constants/config'
import sensors from '../../../utils/sensors'
import { useAppSelector } from '../../../app/hooks'

import YearMonthPicker from './YearMonthPicker'
import VipAlert from './VipAlert'
import vip_team from '../../../images/btn-crown-icon.png'
import { usePayment, useTimeSummary, monthBgImgs } from './hooks'

const today = dayjs()

interface OneDayPaneProps {
  startDate: Dayjs
  endDate: Dayjs
  allowTime?: boolean
  onChange: (startDate: Dayjs, endDate: Dayjs) => void
  onCancel: () => void
  disabledDate?: (date: Dayjs) => boolean
  dataStatisticsType?: string
  isAllPhoto?: boolean
}

const OneDayPane: React.FC<OneDayPaneProps> = (props) => {
  const { onChange, onCancel, dataStatisticsType, isAllPhoto } = props
  // 记录选择的日期
  const [date, setDate] = useState(
    props.startDate.isSame(props.endDate, 'day')
      ? dayjs(props.startDate)
      : dayjs()
  )
  const [startTime, setStartTime] = useState(dayjs(props.startDate)) //记录选择的开始时间（时分）
  const [endTime, setEndTime] = useState(dayjs(props.endDate)) //记录选择的结束时间（时分）
  //记录当前日历的日期（value）
  const [calendarDate, setCalendarDate] = useState(date)

  const onPay = usePayment()

  const isVip = useAppSelector((state) => state.group.isVip)
  const daysLimit = useAppSelector(
    (state) => state.main.config?.normal_group_data_limit_days
  )

  const timeSummary = useTimeSummary(startTime, endTime)

  const isOutOfLimite = useCallback(
    (date: Dayjs) => {
      //超出有效期
      let currentDay = dayjs(date).startOf('day')
      return (
        Math.ceil(today.diff(currentDay, 'days', true)) >
        (parseInt(daysLimit as string) || CONFIG.nomalGroupDatesLimit)
      )
    },
    [daysLimit]
  )

  const isVipAlertVisible = useCallback(() => {
    return (
      !isVip &&
      !isAllPhoto &&
      (isOutOfLimite(dayjs(calendarDate).startOf('month')) ||
        isOutOfLimite(dayjs(calendarDate).endOf('month')))
    )
  }, [calendarDate, isVip, isAllPhoto, isOutOfLimite])

  const onDateChange = useCallback(
    (newDate: Dayjs | null) => {
      if (!newDate) return
      setCalendarDate(newDate)
      setDate(dayjs(newDate))
      if (isVip || isAllPhoto || !isOutOfLimite(newDate)) {
        sensors.track('web_workgroup_filter_time_page_click', {
          clickItem: 'blackDay',
          pagePath: dataStatisticsType
            ? dataStatisticsType
            : window.location.pathname,
          isVip,
          isAllPhoto
        })
      } else {
        sensors.track('web_workgroup_filter_time_page_click', {
          clickItem: 'goldDay',
          pagePath: dataStatisticsType
            ? dataStatisticsType
            : window.location.pathname,
          isVip,
          isAllPhoto
        })
      }
    },
    [isVip, isAllPhoto, isOutOfLimite, dataStatisticsType]
  )

  const onSubmit = useCallback(() => {
    const startDate = dayjs(date)
      .hour(startTime.hour())
      .minute(startTime.minute())
      .second(0)
    const endDate = dayjs(date)
      .hour(endTime.hour())
      .minute(endTime.minute())
      .second(59)
    if (isOutOfLimite(date) && !isVip && !isAllPhoto) {
      sensors.track('web_workgroup_filter_time_page_click', {
        clickItem: 'doneVIP',
        pagePath: dataStatisticsType
          ? dataStatisticsType
          : window.location.pathname,
        isVip: isVip,
        isAllPhoto,
        startTime: startDate.format(CONFIG.timeFormat),
        endTime: endDate.format(CONFIG.timeFormat)
      })
      onPay()
      onCancel()
    } else {
      sensors.track('web_workgroup_filter_time_page_click', {
        clickItem: 'done',
        pagePath: dataStatisticsType
          ? dataStatisticsType
          : window.location.pathname,
        isVip: isVip,
        isAllPhoto,
        startTime: startDate.format(CONFIG.timeFormat),
        endTime: endDate.format(CONFIG.timeFormat)
      })
      onChange(startDate, endDate)
    }
  }, [date, startTime, endTime, isVip, isAllPhoto, onChange, onCancel, dataStatisticsType, isOutOfLimite, onPay])

  const disabledDate = useCallback(
    (current: Dayjs) => {
      //禁止未来日期
      const isAfterToday = current.isAfter(dayjs(), 'day')

      // 如果有外部传入的disabledDate函数，则同时应用两个条件
      if (props.disabledDate) {
        return isAfterToday || props.disabledDate(current)
      }

      return isAfterToday
    },
    [props.disabledDate]
  )

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cellRender = useCallback(
    (current: any, info: { type: string; originNode: React.ReactNode }) => {
      if (info.type !== 'date') {
        return info.originNode
      }
      const isSelectedDay = current.isSame(date, 'day')
      const isOutOfLimiteDate = isOutOfLimite(current) && !isVip && !isAllPhoto
      // 检查是否被禁用（由外部传入的disabledDate函数决定）
      const isDisabledByProps =
        props.disabledDate && props.disabledDate(current)

      const dateCell = (
        <div
          className={classnames('ant-calendar-date', {
            'out-of-limit-days': isOutOfLimiteDate || isDisabledByProps,
            'cus-calendar-selected-date': isSelectedDay,
            'cus-calendar-selected-date-vip': isSelectedDay && isOutOfLimiteDate
          })}
        >
          {current.date()}
        </div>
      )

      if (isDisabledByProps) {
        return <Tooltip title="不可选择此日期">{dateCell}</Tooltip>
      } else if (isOutOfLimiteDate) {
        return <Tooltip title="需开通团队会员">{dateCell}</Tooltip>
      }
      return dateCell
    },
    [date, isVip, isAllPhoto, isOutOfLimite, props.disabledDate]
  )

  return (
    <div className="date-picker-pane">
      <div style={{ display: 'flex' }}>
        <div className="left">
          <YearMonthPicker
            calendarDate={calendarDate}
            onCalendarDateChange={setCalendarDate}
          />
          <div id="one-day-pane-calendar-wrapper" className="calendar-wrapper">
            <DatePicker
              popupClassName="date-picker-dropdown date-picker-dropdown-reset"
              open
              showToday={false}
              value={calendarDate}
              onChange={onDateChange}
              disabledDate={disabledDate}
              cellRender={cellRender}
              getPopupContainer={() =>
                document.getElementById('one-day-pane-calendar-wrapper')!
              }
            />
            <div
              className="date-picker-background"
              style={{
                backgroundImage: `url(${monthBgImgs[calendarDate.month()]})`
              }}
            ></div>
          </div>
        </div>
        <div className="right">
          <div className="accuracy">精确到时分</div>
          {props.allowTime && (
            <div className="time-picker-wrapper">
              <TimePicker
                placeholder="开始时间"
                style={{ width: '100px' }}
                format={CONFIG.hourMinuteFormat}
                allowClear={false}
                defaultValue={
                  startTime.isSame(dayjs(startTime).startOf('day'), 'second')
                    ? null
                    : startTime
                }
                onChange={(time) => {
                  if (time) setStartTime(dayjs(time).second(0))
                }}
              />
              <span style={{ margin: '0 10px' }}>至</span>
              <TimePicker
                placeholder="结束时间"
                style={{ width: '100px' }}
                format={CONFIG.hourMinuteFormat}
                allowClear={false}
                defaultValue={
                  endTime.isSame(dayjs(endTime).endOf('day'), 'second')
                    ? null
                    : endTime
                }
                onChange={(time) => {
                  if (time) setEndTime(dayjs(time).second(59))
                }}
              />
            </div>
          )}
        </div>
      </div>

      <div className="summary">
        {date.format('YYYY-MM-DD')} {timeSummary}
      </div>
      <VipAlert visible={isVipAlertVisible()} onCancel={onCancel} />
      <div className="times-wrapper">
        <Button
          style={{ marginLeft: '48px' }}
          type="default"
          onClick={() => {
            sensors.track('web_workgroup_filter_time_page_click', {
              clickItem: 'cancel',
              pagePath: dataStatisticsType
                ? dataStatisticsType
                : window.location.pathname,
              isVip: isVip,
              isAllPhoto
            })
            onCancel()
          }}
        >
          取消
        </Button>
        <Button
          style={{ marginLeft: '10px' }}
          type="primary"
          onClick={onSubmit}
        >
          确定
          {isOutOfLimite(date) && !isVip && !isAllPhoto && (
            <img style={{ width: '20px' }} src={vip_team} />
          )}
        </Button>
      </div>
    </div>
  )
}

export default OneDayPane
